/** Issues tab helpers: the pickup chart's props, the depth bars and the tile wording. */
import type { Compared, IssueDetail } from '../api/visitorsDesk'
import type { TrendSeriesLine } from '../components/charts/TrendChart'
import { changeText } from './deskFormat'

export type Pickup = NonNullable<IssueDetail['pickup']>

export const EMAIL_MARKER = 'new-issue email sent'

/** TrendChart props: the current issue (first series) is the accent, older ones neutral. */
export function pickupSeries(p: Pickup): { series: TrendSeriesLine[]; labels: string[]; markers: { index: number; label: string }[] } {
  const series = p.series.map((s, i) => ({ label: s.title, values: s.points, emphasis: i === 0 }))
  const n = Math.max(0, ...series.map((s) => s.values.length))
  return {
    series,
    labels: Array.from({ length: n }, (_, i) => `day ${i}`),
    markers: p.email_sent_day === null || p.email_sent_day === undefined ? [] : [{ index: p.email_sent_day, label: EMAIL_MARKER }],
  }
}

/** One bar per tenth of the issue. Bar i is the share of openers whose furthest page is at least
 *  (i+1)/10 of the issue, labelled with that page. `pct` is null below 30 openers.
 *  No usable page count gives no bars. */
export function depthBars(depth: IssueDetail['depth'], pagesTotal: number | null | undefined): { label: string; pct: number | null }[] {
  if (!pagesTotal || pagesTotal <= 0) return []
  return depth.tenths.map((v, i) => ({
    label: `page ${Math.round((pagesTotal * (i + 1)) / 10)}`,
    pct: v === null || v === undefined ? null : Math.round(v * 100),
  }))
}

/** `▲ 9% vs November at the same point`. A "Month YYYY" title reads as the month alone. */
export function tileCompareText(t: Compared, prevTitle: string | null | undefined): string {
  if (!prevTitle) return '— no earlier issue'
  const name = prevTitle.replace(/^([A-Za-z]+)\s+\d{4}$/, '$1')
  return `${changeText(t).text} vs ${name} at the same point`
}

/** `3:10` from milliseconds; a dash when unknown. */
export function mmss(ms: number | null | undefined): string {
  if (ms === null || ms === undefined) return '—'
  const s = Math.round(ms / 1000)
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`
}

/** Where picking an issue goes: its own title's detail page, query string kept. */
export function issueHref(it: { publication: string; issue_id: number }, search: string): string {
  const q = search.replace(/^\?/, '')
  return `/visitors/desk/${it.publication}/issues/${it.issue_id}${q ? `?${q}` : ''}`
}
